import { MouseEvent } from 'react';

import { Box } from '@mui/material';

import { isSubscriptionExpired } from '@/entities/balance/lib/isSubscriptionExpired';
import { useClientStorageFlag } from '@/entities/clientStorage';
import { RECORDING_FIRST_DETAIL_SCREEN_VIEWED_STORAGE_KEY } from '@/entities/recording';
import { useCurrentUser } from '@/entities/user/currentUser';

import giftImage from '@/shared/assets/images/gift.png';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { Subscription } from '@/shared/types/Billing';
import { PromoBanner } from '@/shared/ui/PromoBanner';

import { useRecordingFeedbackBonusModalStore } from '../../model/useRecordingFeedbackBonusModalStore';

// Backend-stored dismissal: clientStorage is authenticated and scoped per-user, so the
// flag does not leak across users on a shared device and survives across browsers/sessions.
const STORAGE_KEY = 'recordingFeedbackBonusBannerDismissed';
const FREE_SUBSCRIPTION_PREFIXES = ['free_sub_', 'sub_free_'];

const isPaidRecordingEligible = (subscription: null | Subscription) => {
  if (!subscription) return false;
  if (FREE_SUBSCRIPTION_PREFIXES.some((prefix) => subscription.id.startsWith(prefix))) return false;

  return (
    subscription.status === 'active' &&
    !subscription.payments_collection_paused &&
    !isSubscriptionExpired(subscription.current_period_end)
  );
};

const GiftIcon = () => (
  <Box alt="" component="img" src={giftImage} sx={{ display: 'block', height: 32, objectFit: 'contain', width: 32 }} />
);

export const RecordingFeedbackBonusBanner = () => {
  const { data: user } = useCurrentUser();
  const open = useRecordingFeedbackBonusModalStore((state) => state.open);

  const { hasShown: hasViewedFirstRecordingDetail, isLoading: isFirstRecordingDetailFlagLoading } =
    useClientStorageFlag(RECORDING_FIRST_DETAIL_SCREEN_VIEWED_STORAGE_KEY, { enabled: Boolean(user) });
  const {
    hasShown: dismissed,
    isLoading: isDismissedFlagLoading,
    markAsShown,
  } = useClientStorageFlag(STORAGE_KEY, { enabled: Boolean(user) });

  const isClaimed = Boolean(user?.recording_feedback_bonus_claimed);
  const isShown =
    Boolean(user) &&
    !isFirstRecordingDetailFlagLoading &&
    !isDismissedFlagLoading &&
    hasViewedFirstRecordingDetail &&
    !isClaimed &&
    !dismissed &&
    !IS_WHITE_LABEL;
  const feedbackBonusMinutes = isPaidRecordingEligible(user?.subscription ?? null) ? 30 : 5;

  if (!isShown) return null;

  const handleOpen = (event: MouseEvent) => {
    event.preventDefault();
    MixpanelEvents.recordingFeedbackBonusCtaClick();
    open();
  };

  return (
    <PromoBanner
      title="Get free minutes!"
      icon={<GiftIcon />}
      link={{ children: `Grab free ${feedbackBonusMinutes} minutes`, to: '#', onClick: handleOpen }}
      variant="primary"
      onClose={markAsShown}
    />
  );
};
